<template>
  <v-row
    no-gutters
    fluid
    style="height: inherit"
  >
    <v-col style="height: inherit">
      <v-data-table
        class="customTable"
        :id="`table--${entityType}`"
        :style="[{'max-height': maxHeight}, tableHeight]"
        :loading="tableLoading"
        fixed-header
        :headers="computedHeaders"
        :items="items"
        :items-per-page.sync="itemsPerPage"
        :page.sync="currentPage"
        :disable-pagination="disablePagination"
        :hide-default-footer="disablePagination"
        :footer-props="{
      'items-per-page-options': [100, 200, 250]
    }"
        v-bar
        :no-data-text="$i18n.t('default.noDataText')"
      >
        <template
          #[computedHeader(header)]
          v-for="header in computedHeaders"
        >
          <span
            v-if="header.text !== 'tools'"
            :key="header.text"
          >{{ getHeaderTitle(header.text) }}</span>
          <span
            v-else
            :key="header.text"
          >
            <v-icon class="subtitle-2 pb-1">mdi-tools</v-icon>
          </span>
        </template>

        <template #body="{items}">
          <tbody id="table__body">
            <tr
              :id="`table-${entityType}-${item.id}`"
              v-for="(item, index) in items"
              :key="index"
              :class="{
                'table__row--selected': item.id === clickedRow && selectInteraction
              }"
              @click="selectInteraction ? onRowClicked(item.id) : rowDetails(item)"
            >
              <td
                @click.stop
                class="table__row--checkbox"
                v-if="selectable"
              >
                <v-checkbox
                  class="table__checkbox small"
                  hide-details
                  color="primary"
                  :key="item.id"
                  v-model="ids"
                  @change="$emit('input', $event)"
                  :value="item.id"
                />
              </td>
              <template v-for="header in headers">
                <slot
                  :name="header.text"
                  :header="header"
                  :item="item"
                >
                  <Attribute
                    :key="header.text"
                    :item="getItemValue(item, header.value)"
                    :is-date="header.type === 'date'"
                    :null-value-key="header.nullValueKey"
                    :translate="header.translate"
                  />
                </slot>
              </template>
              <td
                class="table__row--tools"
                v-if="hasTools"
              >
                <slot
                  name="tools"
                  :item="item"
                  :index="index"
                />
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
    </v-col>
  </v-row>
</template>

<script>
import Attribute from '@/components/dashboard/Attribute';
import sortMixin from '@/mixins/sortMixin';
export default {
  name: 'CustomDataTable',
  mixins: [sortMixin],
  components: {
    Attribute
  },
  props: {
    maxHeight: {
      type: String,
      default: '60vh'
    },
    headers: {
      // tablica obiektów reprezentujących nagłówki, przykładowy obiekt poniżej
      type: Array,
      required: true
    },
    // {
    //     text: 'area', - nazwa klucza dla i18n
    //     value: 'powierzchnia' - nazwa klucza pod którym znajduje się wartość atrybutu w tablicy items
    //     type: 'text',
    //     sortable: false,
    //     translate: false - informacja dla komponentu Attribute (true dla atrybutów mających wartości w dwóch językach)
    //     nullValueKey: 'default.empty' - klucz pustej wartości inny niż domyślny
    //   },
    items: {
      type: Array,
      required: true
    },
    emptyNumberAsZero: {
      type: Boolean,
      required: false,
      default: false
    },
    entityType: {
      type: String,
      required: true
    },
    disablePagination: {
      type: Boolean,
      default: false
    },
    selectable: {
      type: Boolean,
      default: false
    },
    hasTools: {
      type: Boolean,
      default: true
    },
    // props wymagany w sytuacji, w której tabela jest spięta z mapą
    parentClickedRow: {
      type: [Number, String]
    },
    // props wymagany w sytuacji, w której tabela jest spięta z mapą
    selectInteraction: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    itemsPerPage: 100,
    currentPage: 1,
    tableLoading: false,
    ids: []
  }),
  computed: {
    computedHeaders() {
      const headers = [...this.headers].map(h => this.getHeaderConfig(h));
      if (this.selectable) {
        headers.unshift({
          text: null,
          value: 'selectable',
          type: 'text',
          sortable: false
        });
      }
      if (this.hasTools) {
        headers.push({
          text: 'tools',
          value: 'tools',
          align: 'center',
          sortable: false
        });
      }
      return headers;
    },
    tableHeight() {
      return {
        '--table-height': this.disablePagination ? '0px' : '60px'
      };
    },
    clickedRow: {
      get() {
        return this.parentClickedRow;
      },
      set(nV) {
        this.$emit('rowClicked', nV);
      }
    }
  },
  watch: {
    clickedRow(nV) {
      if (this.selectInteraction) {
        if (this.nV !== undefined) {
          const selectedRowNode = document.getElementById(
            `table-offer.parcels-${nV}`
          );
          this.$nextTick(() => {
            selectedRowNode.scrollIntoView({
              block: 'center'
            });
          });
        }
      }
    }
  },
  methods: {
    rowDetails(item) {
      this.$emit('rowDetails', item);
    },
    computedHeader(header) {
      return `header.${header.value}`;
    },
    getHeaderConfig(header) {
      const self = this;
      if (header.type === 'date') {
        return Object.assign(header, {
          sort: (a, b) => self.sortDates(a, b)
        });
      } else if (header.translate) {
        return Object.assign(header, {
          sort: (a, b) => self.sortTranslatedObjects(a, b)
        });
      } else {
        return header;
      }
    },
    clearSelection() {
      this.ids = [];
      this.$emit('input', []);
    },
    onRowClicked(item) {
      this.clickedRow = this.clickedRow === item ? undefined : item;
    },
    getItemValue(nestedObj, path) {
      const pathArr = path.split('.');
      return pathArr.reduce(
        (obj, key) => (obj && obj[key] !== 'undefined' ? obj[key] : undefined),
        nestedObj
      );
    },
    getHeaderTitle(text) {
      if (text) {
        return this.$i18n.t(`${this.entityType}.table.${text}`);
      }
      return null;
    }
  }
};
</script>

<style lang="scss">
.customTable {
  height: 100%;
  .table__row--selected {
    background-color: rgba($primary-color, 0.1) !important;
  }
  .table__checkbox {
    height: 30px;
    width: 10px;
    margin-top: 0;
  }
  // th.text-center {
  // box-shadow: inset 0 -1px 0 rgba(0, 0, 0, 0.12) !important;
  // }
  #table__body > tr {
    // height: 55px;
    td {
      max-width: 100px;
      height: 55px;
      // min-height: 55px;
      // box-sizing: border-box;
      // max-height: 55px;
    }
    // .table__row--tools {
    // max-width: unset;
    // display: flex;
    // justify-content: center;
    // }
  }

  div.v-data-table__wrapper.vb-content {
    height: calc(100% - var(--table-height)) !important;
  }
}
</style>
